import {
  ForwardRefExoticComponent,
  SVGProps,
  useEffect,
  useState,
} from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

export interface TabsGenericProps {
  items: Array<TabItemProps>;
  query?: boolean;
  hasActivityUpdate?: boolean;
  hasChatUpdate?: boolean;
  hugTabs?: boolean;
  customTabs?: boolean;
  plain?: boolean;
  autoSelect?: boolean;
}

export interface TabItemProps {
  label: string;
  icon?: ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  disabled?: boolean;
}

export function TabsGeneric(props: TabsGenericProps) {
  const {
    items,
    query = true,
    hasActivityUpdate,
    hasChatUpdate,
    hugTabs,
    plain,
    customTabs,
    autoSelect = true,
  } = props;
  const navigate = useNavigate();

  const [activeTab, setActive] = useState<number>(autoSelect ? 0 : -1);
  const [paramValue, setParamValue] = useState<string>('');

  const QUERY_PARAM = 'tab';
  const searchParams = new URLSearchParams(window.location.search);

  const setActiveTab = (value: string) => {
    for (let i = 0; i < items.length; i++) {
      const label = String(items[i].label).toLocaleLowerCase();
      if (value.toLocaleLowerCase() === label) {
        setActive(i);
        if (query) {
          navigate({
            search: createSearchParams({ tab: label }).toString(),
          });
        }
      }
    }
  };

  useEffect(() => {
    const currentValue = searchParams.get(QUERY_PARAM) || '';
    if (currentValue !== paramValue) {
      setParamValue(currentValue);
      setActiveTab(currentValue);
    }
  }, [window.location.search, QUERY_PARAM, paramValue]);

  return (
    <div
      className={`
        ${hugTabs || customTabs ? 'flex flex-col items-center' : ''}
        ${customTabs ? 'w-full' : ''}
      `}
    >
      {items && items.length > 1 && (
        <div
          className={`mb-5 flex justify-around overflow-hidden overflow-x-auto text-gray-500 lg:inline-flex lg:justify-start
            ${!hugTabs && !customTabs ? 'w-full' : ''}
            ${customTabs ? 'w-full rounded-xl' : !plain ? 'rounded-md' : ''}
            ${plain ? 'border-b border-gray-200' : 'border shadow-sm'}
          `}
        >
          {items?.map((item, index) => (
            <div
              key={index}
              className={`lg:px-auto flex-grow cursor-pointer border-gray-200 py-4 text-center
                ${customTabs && activeTab !== index ? 'bg-white' : ''}
                ${
                  activeTab === index && !customTabs && !plain
                    ? 'bg-white'
                    : 'bg-gray-100'
                }
                ${
                  plain && activeTab === index
                    ? 'text-primary-800 border-primary-800 border-b-2 font-bold'
                    : ''
                }
                ${index !== items.length - 1 && !plain ? 'border-r-[1px]' : ''}
                ${plain ? 'border-0 border-r-0 bg-transparent' : ''}
                ${item.disabled ? 'cursor-auto opacity-50' : ''} ${
                customTabs && activeTab === index ? 'bg-primary-500' : ''
              }`}
              onClick={() => !item.disabled && setActiveTab(item.label)}
            >
              <div
                className={classNames(
                  'flex items-center justify-center gap-2',

                  {
                    'px-16': hugTabs,
                  },
                  `
                  ${
                    customTabs && activeTab === index
                      ? 'text-white'
                      : 'text-gray-500'
                  }
                  ${customTabs && 'text-lg'}
                  ${!plain ? 'font-bold' : ''}
                  ${plain && activeTab === index ? 'text-primary-800' : ''}
                  `
                )}
              >
                {item.label}
                {item.label === 'Chat' && hasChatUpdate && (
                  <div className="h-[10px] w-[10px] rounded-full bg-red-500"></div>
                )}
                {item.label === 'Activity' && hasActivityUpdate && (
                  <div className="h-[10px] w-[10px] rounded-full bg-red-500"></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="w-full">
        {items && items[activeTab] && items[activeTab].children}
      </div>
    </div>
  );
}

export default TabsGeneric;
